'use client';

import type { OrganizationDetails } from '@goodneighbor/api/routes/organizations/organizations.schema';
import styles from './organization.module.scss';
import { OrganizationHero } from './organization-hero';
import { OrganizationSummary } from './organization-summary';
import { ContactInformationCard } from './organization-contact-card';
import ExpandableText from '@/components/ExpandableText';
import { HostedEventsSection } from './organization-hosted-events';
import { SponsoredEventsSection } from './organization-sponsored-events';
import { ShareDialog, Avatar } from '@goodneighbor/ui';

type OrganizationProps = {
  organization: OrganizationDetails;
};

export default function OrganizationView({ organization }: OrganizationProps) {
  const fallbackShareUrl = `https://goodneighbor.org/organization/${organization.slug}`;
  const shareUrl =
    typeof window === 'undefined' ? fallbackShareUrl : window.location.href;

  return (
    <div className={styles.page}>
      <div className={styles.pageInner}>
        <OrganizationHero
          name={organization.name}
          heroImageUrl={organization.heroImageUrl}
        />
        <div className={styles.content}>
          <div className={styles.headingContainer}>
            {!!organization.logoImageUrl && (
              <Avatar
                name={organization.name}
                src={organization.logoImageUrl}
                className={styles.orgAvatar}
                imageClassName={styles.orgAvatarImage}
              />
            )}
            <div className={styles.headingRow}>
              <h1 className={styles.headingTitle}>{organization.name}</h1>
              <div className={styles.shareButton}>
                <ShareDialog
                  shareUrl={shareUrl}
                  title="Share Organization"
                  description="Encourage your friends to join you in volunteering."
                  messageLabel="Message"
                />
              </div>
            </div>
          </div>
          <div className={styles.layout}>
            <div className={styles.mainContent}>
              <OrganizationSummary
                name={organization.name}
                causeType={organization.causeType}
                skillType={organization.skillType}
                shareUrl={shareUrl}
              />
              <h3 className={styles.sectionHeading}>Partner Description</h3>
              <ExpandableText size="xl">
                {organization?.description
                  ?.split(/\n+/)
                  .map((paragraph) => paragraph.trim())
                  .filter(Boolean)
                  .map((paragraph, index) => (
                    <p className={styles.paragraph} key={index}>
                      {paragraph}
                    </p>
                  ))}
              </ExpandableText>
            </div>
            <aside className={styles.sidebar}>
              <ContactInformationCard organization={organization} />
            </aside>
            <div className={styles.hostedEvents}>
              <HostedEventsSection
                organizationId={organization.id}
                organizationName={organization.name}
              />
            </div>
            <div className={styles.sponsoredEvents}>
              <SponsoredEventsSection organizationId={organization.id} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
